import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useUserProfile } from "../masterLayout/MasterLayoutBody";
import { getLatestImportJobs } from "../api/importJobsAPI";
import { uploadMeterReadingsReport } from "../api/meter-readingsAPI";
import { getIntegrations } from "../api/integrationsAPI";

const IntegrationsLayer = () => {
  const userProfile = useUserProfile();
  const [searchTerm, setSearchTerm] = useState("");
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [integrationsError, setIntegrationsError] = useState(null);
  const [activeIntegrationId, setActiveIntegrationId] = useState(null);
  const [showImportJobsModal, setShowImportJobsModal] = useState(false);
  const [importJobsLoading, setImportJobsLoading] = useState(false);
  const [importJobs, setImportJobs] = useState([]);
  const [importJobsError, setImportJobsError] = useState(null);
  const [selectedImportJob, setSelectedImportJob] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  const getIntegrationKey = (integration) =>
    integration.integrationKey || integration.integrationId || String(integration.id);

  useEffect(() => {
    let isMounted = true;
    setIntegrationsLoading(true);
    setIntegrationsError(null);

    getIntegrations()
      .then((data) => {
        if (!isMounted) {
          return;
        }
        const normalized = Array.isArray(data)
          ? data.map((integration) => ({
            ...integration,
            integrationKey: integration.integrationId || String(integration.id),
            enabled: Boolean(integration.enabled),
          }))
          : [];
        setIntegrations(normalized);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        console.error("Error loading integrations", error);
        setIntegrationsError("Unable to load integrations at the moment.");
      })
      .finally(() => {
        if (isMounted) {
          setIntegrationsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredIntegrations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return integrations;
    }

    return integrations.filter((integration) =>
      integration.title.toLowerCase().includes(term)
    );
  }, [integrations, searchTerm]);

  const toggleIntegration = (integrationId) => {
    setIntegrations((prev) =>
      prev.map((integration) =>
        getIntegrationKey(integration) === integrationId
          ? { ...integration, enabled: !integration.enabled }
          : integration
      )
    );
  };

  const formatImportJobDate = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}-${minutes}`;
  };

  const renderStatusBadge = (status) => {
    if (!status) {
      return <span className="badge bg-secondary">Unknown</span>;
    }

    const normalized = status.toLowerCase();
    const variant = normalized === "success" ? "success" : normalized === "failed" ? "danger" : "secondary";

    return (
      <span className={`badge bg-${variant} text-uppercase`}>{normalized}</span>
    );
  };

  const closeLogModal = () => {
    setShowLogModal(false);
    setSelectedImportJob(null);
  };

  const handleOpenImportJobsModal = () => {
    setShowImportJobsModal(true);
  };

  const handleCloseImportJobsModal = () => {
    setShowImportJobsModal(false);
    closeLogModal();
  };

  const handleViewLog = (job) => {
    if (!job?.log) {
      return;
    }
    setSelectedImportJob(job);
    setShowLogModal(true);
  };

  const handleUploadFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setUploadFile(file);
    setUploadError(null);
    setUploadResult(null);
  };

  const handleUploadReport = async () => {
    if (!uploadFile) {
      setUploadError("Please select a JSON file to upload.");
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    try {
      const result = await uploadMeterReadingsReport(uploadFile);
      setUploadResult(result);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to upload the meter readings report.";
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!showImportJobsModal) {
      return;
    }

    setImportJobsLoading(true);
    setImportJobsError(null);

    getLatestImportJobs()
      .then((data) => {
        setImportJobs(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Error loading import jobs", error);
        setImportJobsError("Unable to load import jobs at the moment.");
      })
      .finally(() => {
        setImportJobsLoading(false);
      });
  }, [showImportJobsModal]);

  const activeIntegration = useMemo(
    () =>
      integrations.find(
        (integration) => getIntegrationKey(integration) === activeIntegrationId
      ),
    [integrations, activeIntegrationId]
  );

  const inboundEmail = useMemo(() => {
    if (userProfile?.db && activeIntegration?.emailSuffix) {
      const db = userProfile.db.replace(/^samurai_/, "");
      const suffix = activeIntegration.emailSuffix.startsWith("@")
        ? activeIntegration.emailSuffix
        : `@${activeIntegration.emailSuffix}`;
      return `${db}${suffix}`;
    }

    if (activeIntegration?.emailSuffix) {
      return `{userProfile.db}${activeIntegration.emailSuffix}`;
    }

    return null;
  }, [activeIntegration, userProfile]);

  const formattedInstructions = useMemo(() => {
    if (!activeIntegration?.instructions) {
      return null;
    }

    return activeIntegration.instructions.replace(/className=/g, "class=");
  }, [activeIntegration]);

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex flex-wrap align-items-center gap-3">
        <div className="d-flex align-items-center gap-2 ms-auto">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2 w-75"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Integration
          </button>
        </div>
      </div>
      <div className="card-body">
        {integrationsLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border" role="status" aria-hidden="true" />
          </div>
        ) : integrationsError ? (
          <div className="alert alert-danger mb-0" role="alert">
            {integrationsError}
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
            {filteredIntegrations.length === 0 ? (
              <div className="col">
                <div className="h-100 d-flex align-items-center justify-content-center text-secondary-500 py-32">
                  No integrations found.
                </div>
              </div>
            ) : (
              filteredIntegrations.map((integration) => {
                const integrationKey = getIntegrationKey(integration);
                return (
                  <div className="col" key={integrationKey}>
                    <div className="card h-100 border border-light-200 shadow-none py-8">
                      <div className="card-body d-flex flex-column">
                        {/* Logo + text block */}
                        <div className="d-flex">
                          <img
                            src={integration.logo}
                            alt={`${integration.title} logo`}
                            className="me-24 flex-shrink-0 rounded-3"
                            style={{
                              width: "60px",
                              height: "auto",
                              maxHeight: "60px",
                              objectFit: "contain",
                              alignSelf: "flex-start" // prevents vertical stretch in flex container
                            }}
                          />

                          <div>
                            <h6 className="text-lg fw-semibold mb-3">{integration.title}</h6>
                            <p className="text-secondary-600 text-sm mb-4">
                              {integration.description}
                            </p>
                            {/*{integration.integrationId === "visionlink" && (
                              <div className="mb-4">
                                <label className="form-label text-sm fw-semibold mb-2">
                                  Upload meter readings JSON
                                </label>
                                <div className="d-flex flex-column gap-2">
                                  <input
                                    type="file"
                                    className="form-control form-control-sm"
                                    accept=".json,application/json"
                                    onChange={handleUploadFileChange}
                                  />
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={handleUploadReport}
                                      disabled={uploading}
                                    >
                                      {uploading ? "Uploading..." : "Upload and Process Report"}
                                    </button>
                                    {uploadFile && (
                                      <span className="text-sm text-secondary-600">
                                        {uploadFile.name}
                                      </span>
                                    )}
                                  </div>
                                  {uploadError && (
                                    <div className="text-danger text-sm">{uploadError}</div>
                                  )}
                                  {uploadResult && (
                                    <div className="text-success text-sm">
                                      {uploadResult.status === "success"
                                        ? `Imported ${uploadResult.rowsInserted ?? 0} of ${uploadResult.rowsIn ?? 0} readings.`
                                        : `Upload status: ${uploadResult.status}`}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}*/}
                          </div>
                        </div>

                        {/* Switch + button row */}
                        <div className="d-flex align-items-center gap-3 mt-auto">
                          {/* Left side: Enable switch */}
                          <div className="form-switch switch-success d-flex align-items-center gap-2 mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`integration-toggle-${integrationKey}`}
                              checked={integration.enabled}
                              onChange={() => toggleIntegration(integrationKey)}
                            />
                            <label
                              className="form-check-label text-sm text-secondary-700"
                              htmlFor={`integration-toggle-${integrationKey}`}
                            >
                              Enable
                            </label>
                          </div>

                          {/* Right side: buttons */}
                          <div className="d-flex gap-2 ms-auto">
                            <button
                              type="button"
                              className="btn btn-outline-primary text-sm btn-sm px-12 radius-8"
                              onClick={handleOpenImportJobsModal}
                            >
                              Import Logs
                            </button>

                            <button
                              type="button"
                              className="btn btn-outline-secondary text-sm btn-sm px-12 radius-8"
                              onClick={() => setActiveIntegrationId(integrationKey)}
                            >
                              Instructions
                            </button>
                          </div>
                        </div>


                      </div>
                    </div>

                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {showImportJobsModal && (
        <div className="modal modal-lg show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Recent Import Jobs</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseImportJobsModal}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                {importJobsLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border" role="status" aria-hidden="true" />
                  </div>
                ) : importJobsError ? (
                  <div className="alert alert-danger mb-0" role="alert">
                    {importJobsError}
                  </div>
                ) : importJobs.length === 0 ? (
                  <div className="text-center text-secondary-600 py-4">
                    No import jobs found for your organisation yet.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead>
                        <tr>
                          <th scope="col">Source</th>
                          <th scope="col">Received At</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="text-end">Rows In</th>
                          <th scope="col" className="text-end">Rows Inserted</th>
                          <th scope="col" className="text-center">Log</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importJobs.map((job) => (
                          <tr key={job.id}>
                            <td>{job.source || "—"}</td>
                            <td>{formatImportJobDate(job.receivedAt)}</td>
                            <td>{renderStatusBadge(job.status)}</td>
                            <td className="text-end">{job.rowsIn ?? 0}</td>
                            <td className="text-end">{job.rowsInserted ?? 0}</td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={!job.log}
                                onClick={() => handleViewLog(job)}
                              >
                                View Log
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseImportJobsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportJobsModal && <div className="modal-backdrop fade show" />}

      {showLogModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ zIndex: 1060 }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content" style={{ maxHeight: "600px" }}>
              <div className="modal-header">
                <h5 className="modal-title">Import Job Log</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeLogModal}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body" style={{ overflowY: "auto" }}>
                <label className="form-label text-sm fw-semibold" htmlFor="importJobLogTextarea">
                  Log Output
                </label>
                <textarea
                  id="importJobLogTextarea"
                  className="form-control"
                  readOnly
                  value={selectedImportJob?.log || ""}
                  style={{ minHeight: "320px" }}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeLogModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLogModal && (
        <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} />
      )}

      {activeIntegration && (
        <div className="modal modal-lg show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Instructions
                  {activeIntegration?.title ? ` - ${activeIntegration.title}` : ""}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setActiveIntegrationId(null)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body text-md text-secondary-700">
                {formattedInstructions ? (
                  <div
                    className="mb-3"
                    dangerouslySetInnerHTML={{ __html: formattedInstructions }}
                  />
                ) : (
                  <p className="mb-3">No instructions available for this integration yet.</p>
                )}

                {inboundEmail && (
                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control"
                      value={inboundEmail}
                      disabled
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => navigator.clipboard.writeText(inboundEmail)}
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveIntegrationId(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

      )}

      {activeIntegration && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default IntegrationsLayer;
